import React from 'react';
import styled from 'styled-components';
import { flattenDeep } from 'lodash';

import Text from 'relements/build/esm/input/text';
import Radio from 'relements/build/esm/input/radio';
import Toggle from 'relements/build/esm/input/toggle';
import Dropdown from 'relements/build/esm/input/dropdown';
import Box from 'relements/build/esm/ui/box';
import TextDisplay from 'relements/build/esm/ui/text';
import Tooltip from 'relements/build/esm/overlays/tooltip';
import Accordion from 'relements/build/esm/ui/accordion';
import InfoIconV4 from 'relements/build/esm/ui/icons/InfoIconV4';

import Slider from '@src/components/Slider';

import { sendGoogleAnalyticsEvents } from '@src/actions/_utils';
import BotStore from '@src/store';
import CommonStore from '@src/stores/CommonStore';
import { GOOGLE_ANALYTICS, FIELD_TO_GA_LABEL_MAPPING } from '@src/const';
import { FEATURE_FLAG_DATA } from '@src/constants/MogamboConstants';
import { getVoiceModels } from '@src/containers/AIAgents/store/api';
import { BOT_ENV } from './constants';
import { SUBTLE_NOD_FREQUENCY_OPTIONS } from './utils';

const STATUS_ACTIVE = { title: 'Active', value: true };
const STATUS_INACTIVE = { title: 'Inactive', value: false };

const TextArea = styled(Text)`
  textarea {
    max-height: 54px;
    overflow-y: auto;
    border: 1px solid
      ${(props) =>
        props.error
          ? props.theme.colors.red.haptik
          : props.theme.colors.grey.haptik};
  }
`;

export default class Advanced extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errorKeys: [],
      dataFetched: false,
      active: STATUS_ACTIVE,
      gender: 'Male',
      companyName: '',
      botPersona: 0,
      botContext: '',
      companyContext: '',
      contextEnhancementInstr: '',
      intentDetectionInstr: '',
      answeringInstr: '',
      aiAgentAuthEnabled: false,
      aiAgentSessionTimeout: null,
      aiAgentSessionTimeoutEnabled: false,
      addDateAndTimeInPrompt: true,
      detectLanguageForAllMessages: false,
      aiAgentVoiceEnabled: false,
      aIAgentVoiceModel: '',
      aiAgentTtsEnabled: false,
      aiAgentPhoneNumber: '',
      aiAgentGreetingMessage: '',
      aiAgentVoiceForOutput: '',
      aiAgentVoiceLocale: '',
      aiAgentVoiceProvider: '',
      aiAgentVoiceModelOptions: [],
      aiAgentVoiceOutputOptions: [],
      ttsVoiceOutputOptions: [],
      aiAgentVoiceLocaleOptions: [],
      aiAgentsModelsData: [],
      aIAgentOptionDisplayStyle: {},
      clearContextOnChatCompletion: false,
      aiAgentOzonetelSipNumber: '',
      aiAgentBackgroundNoiseEnabled: false,
      telephonyPlatformOptions: [],
      aiAgentVoiceTelephonyPlatform: {},
      aiAgentVoiceSubtleNodFrequency: {},
      ttsProviderSpeed: '',
      ttsProviderStability: '',
      ttsProviderStyle: '',
      ttsProviderSimilarityBoost: '',
      ttsProviderUseSpeakerBoost: false,
      aiAgentTextPipelineEnabled: false,
      aiAgentHumanAgentTransferEnabled: false,
      aiAgentToolCallExecutionEnabled: false,
    };
  }

  componentDidMount() {
    if (this.props.active) {
      this.fetchData();
    }
  }

  componentDidUpdate() {
    if (this.props.active && !this.state.dataFetched) {
      this.setState({ dataFetched: true });
      this.fetchData();
    }
  }

  onUpdate = (key, value) => {
    if (key === 'aIAgentVoiceModel') {
      const { aiAgentsModelsData, ttsVoiceOutputOptions } = this.state;
      const aiAgentVoiceProvider = aiAgentsModelsData?.find(
        (model) => model.id === value.id
      )?.meta_data.provider;
      const aiAgentVoiceOutputOptions = aiAgentsModelsData?.reduce(
        (result, model) => {
          if (model.id !== value.id) return result;
          const selectedModelVoices = model.meta_data?.voices?.map((voice) => {
            return { id: voice, name: voice };
          });
          return [...result, ...selectedModelVoices];
        },
        []
      );
      const aiAgentVoiceLocaleOptions = aiAgentsModelsData?.reduce(
        (result, model) => {
          if (model.id !== value.id) return result;
          const selectedModelLocales = model.meta_data.locales
            ? Object.entries(model.meta_data.locales).map(([key, value]) => {
                return { id: value, name: key };
              })
            : [];
          return [...result, ...selectedModelLocales];
        },
        []
      );
      this.setState({
        [key]: value,
        aiAgentVoiceProvider,
        aiAgentVoiceOutputOptions: [
          ...aiAgentVoiceOutputOptions,
          ...ttsVoiceOutputOptions,
        ],
        aiAgentVoiceLocaleOptions,
        aiAgentVoiceForOutput: '',
        aiAgentVoiceLocale: '',
        aiAgentTtsEnabled: false,
      });
    } else if (key === 'aiAgentVoiceForOutput') {
      if ('ttsProviderId' in value) {
        this.setState({
          [key]: value,
          aiAgentTtsEnabled: true,
          ttsProviderSpeed: value.ttsProviderId === 1 ? 1.1 : 1,
        });
      } else {
        this.setState({ [key]: value, aiAgentTtsEnabled: false });
      }
    } else {
      this.setState({ [key]: value });
    }

    if (FIELD_TO_GA_LABEL_MAPPING[key]) {
      sendGoogleAnalyticsEvents(
        GOOGLE_ANALYTICS.CATEGORY.BOT,
        GOOGLE_ANALYTICS.EVENTS.BOT_EDIT,
        FIELD_TO_GA_LABEL_MAPPING[key],
        GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
      );
    }
  };

  validate = () => {
    const errorKeys = [];
    const {
      companyContext,
      botContext,
      contextEnhancementInstr,
      intentDetectionInstr,
      answeringInstr,
      aiAgentSessionTimeout,
      aiAgentSessionTimeoutEnabled,
      aiAgentPhoneNumber,
      aiAgentTextPipelineEnabled,
      aiAgentVoiceForOutput,
      aiAgentTtsEnabled,
    } = this.state;
    const timeoutValueRegex = /^\d+$/;
    const phoneNumberValueRegex = /^\+?\d{7,15}$/;

    if (companyContext && companyContext.split(' ').length > 300)
      errorKeys.push('companyContext');
    if (
      contextEnhancementInstr &&
      contextEnhancementInstr.trim().split(/\s+/).length > 300
    )
      errorKeys.push('contextEnhancementInstr');
    if (
      intentDetectionInstr &&
      intentDetectionInstr.trim().split(/\s+/).length > 300
    )
      errorKeys.push('intentDetectionInstr');
    if (answeringInstr && answeringInstr.trim().split(/\s+/).length > 300)
      errorKeys.push('answeringInstr');
    if (botContext && botContext.split(' ').length > 300)
      errorKeys.push('botContext');
    if (this.state.active === undefined) errorKeys.push('active');
    if (!this.state.gender) errorKeys.push('gender');
    if (this.state.botPersona === undefined) errorKeys.push('botPersona');
    if (aiAgentSessionTimeout && !timeoutValueRegex.test(aiAgentSessionTimeout))
      errorKeys.push('aiAgentSessionTimeout');
    if (aiAgentPhoneNumber && !phoneNumberValueRegex.test(aiAgentPhoneNumber))
      errorKeys.push('aiAgentPhoneNumber');
    if (
      aiAgentSessionTimeoutEnabled &&
      (!aiAgentSessionTimeout || Number(aiAgentSessionTimeout) < 1)
    )
      errorKeys.push('aiAgentSessionTimeout');

    if (
      aiAgentTextPipelineEnabled &&
      !aiAgentTtsEnabled &&
      !aiAgentVoiceForOutput?.name?.includes('ElevenLabs') &&
      !aiAgentVoiceForOutput?.name?.includes('Azure')
    )
      errorKeys.push('aiAgentVoiceForOutput');

    this.setState({ errorKeys });

    return errorKeys.length === 0;
  };

  async fetchData() {
    const domain =
      (CommonStore.getMetaTags().domain?.length &&
        CommonStore.getMetaTags().domain) ||
      JSON.parse(localStorage.getItem('ww_iva'));
    const domainId = domain?.id;
    if (!domainId) return;
    try {
      const { response } = await getVoiceModels();
      if (response.success) {
        const data = response.body.models;
        const ttsData = response.body.tts;
        const telephonyPlatformOptions = response.body.telephony_platforms?.map(
          (platform) => ({ name: platform })
        );
        const aiAgentVoiceModelOptions = data.reduce((result, model) => {
          const newModel = { id: model.id, name: model.name };
          return [...result, newModel];
        }, []);
        const aiAgentVoiceOutputOptions = data.reduce((result, model) => {
          if (model.id !== this.state.aIAgentVoiceModel.id) return result;

          const selectedModelVoices =
            model.meta_data?.voices?.map((voice) => {
              return { id: voice, name: voice };
            }) || [];
          return [...result, ...selectedModelVoices];
        }, []);
        let ttsVoiceOutputOptions = Object.entries(ttsData)
          .map(([ttsProviderId, ttsVoiceData]) => ({
            ...ttsVoiceData,
            ttsProviderId: parseInt(ttsProviderId, 10),
          }))
          .map((voiceData) => {
            const voiceOptions = voiceData.voice_details.map((voice) => ({
              id: voice.voice_id,
              name: `${voice.voice_name} — ${voiceData.provider_name}`,
              ttsProviderId: voiceData.ttsProviderId,
            }));
            return voiceOptions;
          });
        ttsVoiceOutputOptions = flattenDeep(ttsVoiceOutputOptions);
        const aiAgentVoiceLocaleOptions = data.reduce((result, model) => {
          if (model.id !== this.state.aIAgentVoiceModel.id) return result;
          const selectedModelLocales = model.meta_data.locales
            ? Object.entries(model.meta_data.locales).map(([key, value]) => {
                return { id: value, name: key };
              })
            : [];

          return [...result, ...selectedModelLocales];
        }, []);
        let aiAgentVoiceLocale = this.state.aiAgentVoiceLocale;

        aiAgentVoiceLocaleOptions.forEach((locale) => {
          if (locale.id === aiAgentVoiceLocale) {
            aiAgentVoiceLocale = { id: aiAgentVoiceLocale, name: locale.name };
          }
        });
        this.setState({
          aiAgentsModelsData: data,
          aiAgentVoiceModelOptions,
          aiAgentVoiceOutputOptions: [
            ...aiAgentVoiceOutputOptions,
            ...ttsVoiceOutputOptions,
          ],
          aiAgentVoiceLocaleOptions,
          aiAgentVoiceLocale,
          ttsVoiceOutputOptions,
          telephonyPlatformOptions,
        });
      }
    } catch (error) {
      console.error('Error fetching AI agent data:', error);
    }
  }

  render() {
    const {
      aiAgentVoiceEnabled,
      aiAgentVoiceForOutput,
      aiAgentOzonetelSipNumber,
      ttsProviderSimilarityBoost,
      ttsProviderSpeed,
      ttsProviderStability,
      ttsProviderStyle,
      ttsProviderUseSpeakerBoost,
      aiAgentTtsEnabled,
      aiAgentVoiceTelephonyPlatform,
      aiAgentHumanAgentTransferEnabled,
      aiAgentToolCallExecutionEnabled,
    } = this.state;
    const { active_env } = this.props.botEnv;
    const ENV_OBJ =
      active_env && BOT_ENV.hasOwnProperty(active_env.toUpperCase())
        ? BOT_ENV[active_env.toUpperCase()]
        : BOT_ENV.TEST;
    const isAissIntentEnable = BotStore.getFeatureFlag(
      FEATURE_FLAG_DATA.FLAGS.ENABLE_AISS_INTENT
    );
    const currentDomainInStore = CommonStore.getMetaTags().domain;

    return (
      <div>
        <Accordion>
          <Accordion.Item
            active={this.props.active}
            onChange={this.props.onChange}
            renderHeader={this.props.renderHeader}
            className={this.props.className}
            prefixClassName={this.props.prefixClassName}
          >
            <div className="grid-container">
              <div className="grid-label">
                * Bot deployment environment (base URL)
              </div>
              <div className="radio-wrapper">
                <Radio
                  themed
                  prefixClassName="env-options"
                  value={ENV_OBJ}
                  options={Object.values(BOT_ENV)}
                  onChange={(value) =>
                    this.props.handleEnvUpdate(`active_env`, value.value)
                  }
                />
              </div>
              <div className="input-wrapper">
                <Text
                  themed
                  style={{ marginBottom: 8 }}
                  className="url-input"
                  placeholder={BOT_ENV.TEST.placeholder}
                  value={this.props.botEnv[`base_url_${BOT_ENV.TEST.value}`]}
                  onChange={(value) =>
                    this.props.handleEnvUpdate(
                      `base_url_${BOT_ENV.TEST.value}`,
                      value
                    )
                  }
                />
                <Text
                  themed
                  className="url-input"
                  placeholder={BOT_ENV.PRODUCTION.placeholder}
                  value={
                    this.props.botEnv[`base_url_${BOT_ENV.PRODUCTION.value}`]
                  }
                  onChange={(value) =>
                    this.props.handleEnvUpdate(
                      `base_url_${BOT_ENV.PRODUCTION.value}`,
                      value
                    )
                  }
                />
              </div>
            </div>
            <Radio
              themed
              options={this.props.personalities}
              label="* Personality"
              value={this.props.personalities[this.state.botPersona]}
              onChange={(value) => this.onUpdate('botPersona', value.key)}
              error={this.state.errorKeys.indexOf('botPersona') > -1}
              className="bot-properties-input"
            />
            <Radio
              themed
              options={[STATUS_ACTIVE, STATUS_INACTIVE]}
              label="* Bot Status"
              className="bot-properties-input"
              value={this.state.active}
              onChange={(value) => this.onUpdate('active', value)}
              error={this.state.errorKeys.indexOf('active') > -1}
            />
            <Text
              themed
              label="* Gender"
              placeholder="Write here..."
              className="bot-properties-input"
              value={this.state.gender}
              onChange={(value) => this.onUpdate('gender', value)}
              error={this.state.errorKeys.indexOf('gender') > -1}
            />
            <Text
              themed
              label="Name of the Creator / Company"
              placeholder="(Optional) Write here..."
              className="bot-properties-input"
              value={this.state.companyName}
              onChange={(value) => this.onUpdate('companyName', value)}
            />
            <Text
              themed
              label="Name of the app"
              placeholder="(Optional) Write here..."
              className="bot-properties-input"
              value={this.state.appName}
              onChange={(value) => this.onUpdate('appName', value)}
            />
            <Text
              themed
              label="Feedback Smart Action Link"
              placeholder="(Optional) Write here..."
              className="bot-properties-input"
              value={this.state.feedbackSmartActionLink}
              onChange={(value) =>
                this.onUpdate('feedbackSmartActionLink', value)
              }
            />
            <Text
              themed
              label="Company Website"
              placeholder="(Optional) Write here..."
              className="bot-properties-input"
              value={this.state.companyWebsite}
              onChange={(value) => this.onUpdate('companyWebsite', value)}
            />
            <Text
              themed
              label="Where does the bot live?"
              placeholder="(Optional) Write here..."
              className="bot-properties-input"
              value={this.state.botLocation}
              onChange={(value) => this.onUpdate('botLocation', value)}
            />
            <TextArea
              multiline
              themed
              label="Bot Description (Max 300 Words)"
              placeholder="(Optional) Describe the purpose of the bot in a few words here..."
              className="bot-properties-input"
              value={this.state.botContext}
              onChange={(value) => {
                this.onUpdate('botContext', value);
              }}
              error={this.state.errorKeys.indexOf('botContext') > -1}
            />
            <TextArea
              multiline
              themed
              label="Company Description (Max 300 Words)"
              placeholder="(Optional) Write a brief note about the Company, and the services it provides, here..."
              className="bot-properties-input"
              value={this.state.companyContext}
              onChange={(value) =>
                this.onUpdate('companyContext', value.trim())
              }
              error={this.state.errorKeys.indexOf('companyContext') > -1}
            />
            <TextArea
              multiline
              themed
              label="Context Enhancement Instructions (Max 300 Words)"
              placeholder="(Optional) Write a brief instruction you want to provide to GPT during context enhancement."
              className="bot-properties-input"
              value={this.state.contextEnhancementInstr}
              onChange={(value) =>
                this.onUpdate('contextEnhancementInstr', value.trim())
              }
              error={
                this.state.errorKeys.indexOf('contextEnhancementInstr') > -1
              }
              border
            />
            {isAissIntentEnable && (
              <TextArea
                multiline
                themed
                label="Intent Detection Instructions (Max 300 Words)"
                placeholder="(Optional) Write a brief instruction you want to provide to GPT during intent detection."
                className="bot-properties-input"
                value={this.state.intentDetectionInstr}
                onChange={(value) =>
                  this.onUpdate('intentDetectionInstr', value.trim())
                }
                error={
                  this.state.errorKeys.indexOf('intentDetectionInstr') > -1
                }
              />
            )}
            <TextArea
              multiline
              themed
              label="AISS Answering/Inference Instructions (Max 300 Words)"
              placeholder="(Optional) Write a brief instruction you want to provide to GPT during answering/inference."
              className="bot-properties-input"
              value={this.state.answeringInstr}
              onChange={(value) =>
                this.onUpdate('answeringInstr', value.trim())
              }
              error={this.state.errorKeys.indexOf('answeringInstr') > -1}
            />
            {currentDomainInStore.domain_type === 2 && (
              <>
                <Toggle
                  themed
                  value={this.state.aiAgentAuthEnabled}
                  onChange={(value) =>
                    this.onUpdate('aiAgentAuthEnabled', value)
                  }
                  label="Authentication"
                />
                <Toggle
                  themed
                  disabled={!this.state.aiAgentAuthEnabled}
                  value={this.state.aiAgentSessionTimeoutEnabled}
                  onChange={(value) =>
                    this.onUpdate('aiAgentSessionTimeoutEnabled', value)
                  }
                  label="Session Timeout"
                />
                <Text
                  themed
                  disabled={
                    !this.state.aiAgentAuthEnabled ||
                    !this.state.aiAgentSessionTimeoutEnabled
                  }
                  label="Duration of the Session in minutes"
                  placeholder="Session Duration in Minutes"
                  className="bot-properties-input"
                  value={this.state.aiAgentSessionTimeout}
                  onChange={(value) => {
                    if (!value?.trim())
                      this.onUpdate('aiAgentSessionTimeout', null);
                    else this.onUpdate('aiAgentSessionTimeout', value?.trim());
                  }}
                  error={
                    this.state.errorKeys.indexOf('aiAgentSessionTimeout') > -1
                  }
                />
                <Toggle
                  themed
                  value={this.state.addDateAndTimeInPrompt}
                  onChange={(value) =>
                    this.onUpdate('addDateAndTimeInPrompt', value)
                  }
                  label="Keep AI Agent aware of Date and Time"
                />
              </>
            )}
            <Toggle
              themed
              value={this.state.detectLanguageForAllMessages}
              onChange={(value) =>
                this.onUpdate('detectLanguageForAllMessages', value)
              }
              label="Detect language for all messages"
            />

            {currentDomainInStore.domain_type === 2 && (
              <>
                <Toggle
                  themed
                  value={this.state.clearContextOnChatCompletion}
                  onChange={(value) =>
                    this.onUpdate('clearContextOnChatCompletion', value)
                  }
                  label="Clear Context on Chat Completion"
                />
                <Dropdown
                  themed
                  options={[
                    { name: 'None', id: 0 },
                    { name: 'Quick Replies', id: 1 },
                    { name: 'Buttons', id: 2 },
                  ]}
                  className="bot-properties-input"
                  label={
                    <Box padding="0" alignItems="center">
                      <TextDisplay color="black.haptik" variant="h4.semi-bold">
                        Option Display Style
                      </TextDisplay>
                      <Tooltip
                        themed
                        tooltip="Choose how the bot shows selectable options — as buttons or quick replies."
                        position="TOP"
                      >
                        <InfoIconV4
                          size="small"
                          iconColor="grey.dark"
                          marginLeft="4px"
                        />
                      </Tooltip>
                    </Box>
                  }
                  disabled={BotStore.isProductionEnv()}
                  placeholder="Select Formatting"
                  optionKey="name"
                  value={
                    this.state.aIAgentOptionDisplayStyle.name
                      ? this.state.aIAgentOptionDisplayStyle
                      : [
                          { name: 'None', id: 0 },
                          { name: 'Quick Replies', id: 1 },
                          { name: 'Buttons', id: 2 },
                        ].find(
                          (option) =>
                            option.id ===
                            this.state.aIAgentOptionDisplayStyle.id
                        )
                  }
                  onChange={(value) =>
                    this.onUpdate('aIAgentOptionDisplayStyle', value)
                  }
                />
              </>
            )}
            {currentDomainInStore.domain_type === 2 && (
              <>
                <Toggle
                  themed
                  value={this.state.aiAgentVoiceEnabled}
                  onChange={(value) =>
                    this.onUpdate('aiAgentVoiceEnabled', value)
                  }
                  label="Enable Voice"
                />
                <Dropdown
                  themed
                  withSearch
                  options={this.state.aiAgentVoiceModelOptions || []}
                  className="bot-properties-input"
                  label="Select Model"
                  disabled={
                    BotStore.isProductionEnv() ||
                    !this.state.aiAgentVoiceEnabled
                  }
                  hint="You can add more languages after the bot has been created."
                  placeholder="Select items"
                  optionKey="name"
                  searchKeys={['name']}
                  value={
                    this.state.aIAgentVoiceModel.name
                      ? this.state.aIAgentVoiceModel
                      : this.state.aiAgentVoiceModelOptions.find(
                          (model) =>
                            model.id === this.state.aIAgentVoiceModel.id
                        )
                  }
                  onChange={(value) =>
                    this.onUpdate('aIAgentVoiceModel', value)
                  }
                />
                <Dropdown
                  themed
                  withSearch
                  options={this.state.telephonyPlatformOptions || []}
                  className="bot-properties-input"
                  label="Select Telephony Provider"
                  disabled={
                    BotStore.isProductionEnv() ||
                    !this.state.aiAgentVoiceTelephonyPlatform ||
                    !aiAgentVoiceEnabled
                  }
                  hint="You can add more languages after the bot has been created."
                  placeholder="Select items"
                  optionKey="name"
                  searchKeys={['name']}
                  value={this.state.aiAgentVoiceTelephonyPlatform?.name}
                  onChange={(value) => {
                    this.onUpdate('aiAgentVoiceTelephonyPlatform', value);
                    if (
                      !['tata_telephony', 'exotel'].includes(value.name) &&
                      aiAgentHumanAgentTransferEnabled
                    ) {
                      this.onUpdate('aiAgentHumanAgentTransferEnabled', false);
                    }
                  }}
                />

                {/* <Toggle
              themed
              value={this.state.aiAgentTtsEnabled}
              onChange={(value) => this.onUpdate('aiAgentTtsEnabled', value)}
              label="Enable TTS"
              disabled={!this.state.aiAgentVoiceEnabled}
            /> */}
                <Text
                  themed
                  disabled={
                    BotStore.isProductionEnv() ||
                    !this.state.aiAgentVoiceEnabled
                  }
                  label="Phone Number"
                  placeholder="Phone Number"
                  className="bot-properties-input"
                  value={this.state.aiAgentPhoneNumber}
                  onChange={(value) => {
                    if (!value?.trim()) this.onUpdate('aiAgentPhoneNumber', '');
                    else this.onUpdate('aiAgentPhoneNumber', value?.trim());
                  }}
                  error={
                    this.state.errorKeys.indexOf('aiAgentPhoneNumber') > -1 &&
                    'Please enter a valid phone number.'
                  }
                  errorText
                />
                <Text
                  themed
                  disabled={
                    BotStore.isProductionEnv() ||
                    !this.state.aiAgentVoiceEnabled ||
                    this.state.aiAgentVoiceTelephonyPlatform?.name !==
                      'ozonetel'
                  }
                  label="Sip Registration Number"
                  placeholder="Sip Registration Number"
                  className="bot-properties-input"
                  value={aiAgentOzonetelSipNumber}
                  onChange={(value) => {
                    if (!value?.trim())
                      this.onUpdate('aiAgentOzonetelSipNumber', '');
                    else
                      this.onUpdate('aiAgentOzonetelSipNumber', value?.trim());
                  }}
                />
                <Text
                  themed
                  disabled={
                    BotStore.isProductionEnv() ||
                    !this.state.aiAgentVoiceEnabled
                  }
                  label="Greeting Message"
                  placeholder="Greeting Message"
                  className="bot-properties-input"
                  value={this.state.aiAgentGreetingMessage}
                  onChange={(value) => {
                    if (!value?.trim())
                      this.onUpdate('aiAgentGreetingMessage', '');
                    else this.onUpdate('aiAgentGreetingMessage', value?.trim());
                  }}
                  error={
                    this.state.errorKeys.indexOf('aiAgentGreetingMessage') > -1
                  }
                />
                <Toggle
                  themed
                  value={this.state.aiAgentTextPipelineEnabled}
                  onChange={(value) =>
                    this.onUpdate('aiAgentTextPipelineEnabled', value)
                  }
                  label="Enable Text Pipeline"
                  disabled={BotStore.isProductionEnv() || !aiAgentVoiceEnabled}
                />
                <Dropdown
                  themed
                  withSearch
                  options={this.state.aiAgentVoiceOutputOptions || []}
                  className="bot-properties-input"
                  label="Voice for Output"
                  disabled={
                    BotStore.isProductionEnv() ||
                    !this.state.aiAgentVoiceEnabled
                  }
                  hint="You can add more languages after the bot has been created."
                  placeholder="Select items"
                  optionKey="name"
                  searchKeys={['name']}
                  value={aiAgentVoiceForOutput || {}}
                  onChange={(value) =>
                    this.onUpdate('aiAgentVoiceForOutput', value)
                  }
                  inputStyles={{
                    borderColor:
                      this.state.errorKeys.indexOf('aiAgentVoiceForOutput') >
                        -1 && '#D91E2D',
                  }}
                  error={
                    this.state.errorKeys.indexOf('aiAgentVoiceForOutput') >
                      -1 && 'Please select tts voice for text pipeline.'
                  }
                />
                <Dropdown
                  themed
                  withSearch
                  options={this.state.aiAgentVoiceLocaleOptions}
                  className="bot-properties-input"
                  label="Locale"
                  disabled={
                    BotStore.isProductionEnv() ||
                    !this.state.aiAgentVoiceEnabled ||
                    !this.state.aiAgentVoiceLocaleOptions?.length ||
                    this.state.aiAgentTtsEnabled
                  }
                  placeholder="Select locale"
                  optionKey="name"
                  searchKeys={['name']}
                  value={this.state.aiAgentVoiceLocale || {}}
                  onChange={(value) =>
                    this.onUpdate('aiAgentVoiceLocale', value)
                  }
                />
                <Toggle
                  themed
                  value={this.state.aiAgentBackgroundNoiseEnabled}
                  onChange={(value) =>
                    this.onUpdate('aiAgentBackgroundNoiseEnabled', value)
                  }
                  disabled={
                    BotStore.isProductionEnv() ||
                    !this.state.aiAgentVoiceEnabled
                  }
                  label={
                    <Box padding="0" alignItems="center">
                      <TextDisplay color="black.haptik" variant="h4.semi-bold">
                        Enable Background Ambience
                      </TextDisplay>
                      <Tooltip
                        themed
                        tooltip="Adds subtle office background sounds when the AI agent speaks, making conversations feel more natural and human-like."
                        position="TOP"
                      >
                        <InfoIconV4
                          size="small"
                          iconColor="grey.dark"
                          marginLeft="4px"
                        />
                      </Tooltip>
                    </Box>
                  }
                />
                {['tata_telephony', 'exotel'].includes(
                  aiAgentVoiceTelephonyPlatform.name
                ) && (
                  <Toggle
                    themed
                    value={aiAgentHumanAgentTransferEnabled}
                    onChange={(value) =>
                      this.onUpdate('aiAgentHumanAgentTransferEnabled', value)
                    }
                    disabled={
                      BotStore.isProductionEnv() || !aiAgentVoiceEnabled
                    }
                    label={
                      <Box padding="0" alignItems="center">
                        <TextDisplay
                          color="black.haptik"
                          variant="h4.semi-bold"
                        >
                          Enable Human Agent Transfer
                        </TextDisplay>
                        <Tooltip
                          themed
                          tooltip="Allows the voice agent to transfer the call to a human agent when required."
                          position="TOP"
                        >
                          <InfoIconV4
                            size="small"
                            iconColor="grey.dark"
                            marginLeft="4px"
                          />
                        </Tooltip>
                      </Box>
                    }
                  />
                )}
                <Toggle
                  themed
                  value={aiAgentToolCallExecutionEnabled}
                  onChange={(value) =>
                    this.onUpdate('aiAgentToolCallExecutionEnabled', value)
                  }
                  disabled={BotStore.isProductionEnv() || !aiAgentVoiceEnabled}
                  label={
                    <Box padding="0" alignItems="center">
                      <TextDisplay color="black.haptik" variant="h4.semi-bold">
                        Enable Tool call acknowledgement
                      </TextDisplay>
                      <Tooltip
                        themed
                        tooltip='When enabled, the voice agent will confirm tool call actions (e.g., "Stand by, please.") before executing them.'
                        position="TOP"
                      >
                        <InfoIconV4
                          size="small"
                          iconColor="grey.dark"
                          marginLeft="4px"
                        />
                      </Tooltip>
                    </Box>
                  }
                />
                <Dropdown
                  themed
                  withSearch
                  options={SUBTLE_NOD_FREQUENCY_OPTIONS}
                  className="bot-properties-input"
                  label={
                    <Box padding="0" alignItems="center">
                      <TextDisplay color="black.haptik" variant="h4.semi-bold">
                        Subtle Nod Frequency
                      </TextDisplay>
                      <Tooltip
                        themed
                        tooltip="Control how often the chatbot gives brief, natural acknowledgements (called “Subtle Nods”) such as “Got it!” or “Sure thing!”. These nods make conversations feel more natural without sounding repetitive."
                        position="TOP"
                      >
                        <InfoIconV4
                          size="small"
                          iconColor="grey.dark"
                          marginLeft="4px"
                        />
                      </Tooltip>
                    </Box>
                  }
                  disabled={
                    BotStore.isProductionEnv() ||
                    !this.state.aiAgentVoiceEnabled ||
                    !this.state.aiAgentTextPipelineEnabled
                  }
                  placeholder="Select Subtle Nod Frequency"
                  optionKey="name"
                  searchKeys={['name']}
                  value={this.state.aiAgentVoiceSubtleNodFrequency || {}}
                  onChange={(value) =>
                    this.onUpdate('aiAgentVoiceSubtleNodFrequency', value)
                  }
                />
                {(aiAgentTtsEnabled ||
                  aiAgentVoiceForOutput?.ttsProviderId === 1) && (
                  <Box
                    padding="0"
                    flexDirection="column"
                    style={{ gap: '20px' }}
                  >
                    <Box padding="0" alignItems="center">
                      <TextDisplay color="black.haptik" variant="h4.semi-bold">
                        Speed
                      </TextDisplay>
                      <Tooltip
                        themed
                        tooltip="Adjusts how fast or slow the generated speech is delivered. (Default: 1.0)"
                        position="TOP"
                      >
                        <InfoIconV4
                          size="small"
                          iconColor="grey.dark"
                          marginLeft="4px"
                        />
                      </Tooltip>
                    </Box>
                    <Slider
                      min={
                        aiAgentVoiceForOutput?.ttsProviderId === 1 ? 0.5 : 0.7
                      }
                      max={
                        aiAgentVoiceForOutput?.ttsProviderId === 1 ? 2.0 : 1.2
                      }
                      step={0.05}
                      leftLabel={
                        aiAgentVoiceForOutput?.ttsProviderId === 1
                          ? '0.5'
                          : '0.7'
                      }
                      rightLabel={
                        aiAgentVoiceForOutput?.ttsProviderId === 1
                          ? '2.0'
                          : '1.2'
                      }
                      labelMarginTop="-7px"
                      value={ttsProviderSpeed}
                      onChange={(e) =>
                        this.onUpdate(
                          'ttsProviderSpeed',
                          parseFloat(e.target.value)
                        )
                      }
                      width="80%"
                      style={{ margin: '0 auto' }}
                      disabled={
                        BotStore.isProductionEnv() ||
                        !aiAgentTtsEnabled ||
                        !aiAgentVoiceEnabled
                      }
                    />
                  </Box>
                )}
                {aiAgentVoiceForOutput?.ttsProviderId === 0 && (
                  <>
                    <Box
                      padding="0"
                      flexDirection="column"
                      style={{ gap: '20px' }}
                    >
                      <Box padding="0" alignItems="center">
                        <TextDisplay
                          color="black.haptik"
                          variant="h4.semi-bold"
                        >
                          Stability
                        </TextDisplay>
                        <Tooltip
                          themed
                          tooltip="Controls consistency of tone and pacing across the speech. (Default: 0.5)"
                          position="TOP"
                        >
                          <InfoIconV4
                            size="small"
                            iconColor="grey.dark"
                            marginLeft="4px"
                          />
                        </Tooltip>
                      </Box>
                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        leftLabel="0"
                        rightLabel="1"
                        labelMarginTop="-7px"
                        value={ttsProviderStability}
                        onChange={(e) =>
                          this.onUpdate(
                            'ttsProviderStability',
                            parseFloat(e.target.value)
                          )
                        }
                        width="80%"
                        style={{ margin: '0 auto' }}
                        disabled={
                          BotStore.isProductionEnv() ||
                          !aiAgentVoiceForOutput?.name?.includes(
                            'ElevenLabs'
                          ) ||
                          !aiAgentVoiceEnabled
                        }
                      />
                    </Box>
                    <Box
                      padding="0"
                      flexDirection="column"
                      style={{ gap: '20px' }}
                    >
                      <Box padding="0" alignItems="center">
                        <TextDisplay
                          color="black.haptik"
                          variant="h4.semi-bold"
                        >
                          Style
                        </TextDisplay>
                        <Tooltip
                          themed
                          tooltip="Adds expressive variation to the voice. (Default: 0.0)"
                          position="TOP"
                        >
                          <InfoIconV4
                            size="small"
                            iconColor="grey.dark"
                            marginLeft="4px"
                          />
                        </Tooltip>
                      </Box>
                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        leftLabel="0"
                        rightLabel="1"
                        labelMarginTop="-7px"
                        value={ttsProviderStyle}
                        onChange={(e) =>
                          this.onUpdate(
                            'ttsProviderStyle',
                            parseFloat(e.target.value)
                          )
                        }
                        width="80%"
                        style={{ margin: '0 auto' }}
                        disabled={
                          BotStore.isProductionEnv() ||
                          !aiAgentVoiceForOutput?.name?.includes(
                            'ElevenLabs'
                          ) ||
                          !aiAgentVoiceEnabled
                        }
                      />
                    </Box>
                    <Box
                      padding="0"
                      flexDirection="column"
                      style={{ gap: '20px' }}
                    >
                      <Box padding="0" alignItems="center">
                        <TextDisplay
                          color="black.haptik"
                          variant="h4.semi-bold"
                        >
                          Similarity Boost
                        </TextDisplay>
                        <Tooltip
                          themed
                          tooltip="Makes the output voice sound closer to the reference voice. (Default: 0.75)"
                          position="TOP"
                        >
                          <InfoIconV4
                            size="small"
                            iconColor="grey.dark"
                            marginLeft="4px"
                          />
                        </Tooltip>
                      </Box>

                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        leftLabel="0"
                        rightLabel="1"
                        labelMarginTop="-7px"
                        value={ttsProviderSimilarityBoost}
                        onChange={(e) =>
                          this.onUpdate(
                            'ttsProviderSimilarityBoost',
                            parseFloat(e.target.value)
                          )
                        }
                        width="80%"
                        style={{ margin: '0 auto' }}
                        disabled={
                          BotStore.isProductionEnv() ||
                          !aiAgentVoiceForOutput?.name?.includes(
                            'ElevenLabs'
                          ) ||
                          !aiAgentVoiceEnabled
                        }
                      />
                    </Box>

                    <Toggle
                      themed
                      value={ttsProviderUseSpeakerBoost}
                      onChange={(value) =>
                        this.onUpdate('ttsProviderUseSpeakerBoost', value)
                      }
                      disabled={
                        BotStore.isProductionEnv() ||
                        !aiAgentVoiceForOutput?.name?.includes('ElevenLabs') ||
                        !aiAgentVoiceEnabled
                      }
                      label={
                        <Box padding="0" alignItems="center">
                          <TextDisplay
                            color="black.haptik"
                            variant="h4.semi-bold"
                          >
                            Use Speaker Boost
                          </TextDisplay>
                          <Tooltip
                            themed
                            tooltip="Enhances clarity and presence of the chosen speaker’s voice."
                            position="TOP"
                          >
                            <InfoIconV4
                              size="small"
                              iconColor="grey.dark"
                              marginLeft="4px"
                            />
                          </Tooltip>
                        </Box>
                      }
                    />
                  </>
                )}
              </>
            )}
          </Accordion.Item>
        </Accordion>
      </div>
    );
  }
}
